/**
 * Cyber UI tokens and MUI `sx` presets for ranking / dialog chrome.
 * Import presets into existing components; do not wrap MUI primitives.
 */

export const CYBER_FONT = "Noto Sans TC";

export const CHOICE_BUTTON_FONT_SIZE = 20;

export const cyberColors = {
    purple: {
        faint: "rgba(130, 60, 255, 0.08)",
        border: "rgba(130, 60, 255, 0.25)",
        borderMedium: "rgba(130, 60, 255, 0.3)",
        glow: "rgba(130, 60, 255, 0.4)",
        borderStrong: "rgba(130, 60, 255, 0.5)",
        borderBold: "rgba(130, 60, 255, 0.7)",
        hoverBg: "rgba(130, 60, 255, 0.25)",
        activeBorder: "rgba(130, 60, 255, 0.8)",
        activeFill: "rgba(130, 60, 255, 0.35)",
        insetGlow: "rgba(130, 60, 255, 0.05)",
        cardGlow: "rgba(130, 60, 255, 0.3)",
    },
    surface: {
        dark: "rgba(10, 5, 30, 0.7)",
        darker: "rgba(10, 5, 30, 0.95)",
        panel: "rgba(10, 5, 30, 0.5)",
    },
    cyan: "#00E5FF",
    cyanAlpha: {
        faint: "rgba(0, 229, 255, 0.1)",
        subtle: "rgba(0, 229, 255, 0.4)",
        medium: "rgba(0, 229, 255, 0.5)",
        strong: "rgba(0, 229, 255, 0.6)",
        glow: "rgba(0, 229, 255, 0.7)",
        glowStrong: "rgba(0, 229, 255, 0.8)",
    },
    muted: {
        text: "rgba(180, 160, 220, 0.7)",
        textBright: "rgba(180, 160, 220, 0.8)",
    },
    text: {
        empty: "#8888AA",
        white: "#FFFFFF",
        light: "#E0D0FF",
        disabled: "#999999",
    },
    gradient: {
        heading: "linear-gradient(90deg, #00FF88, #00E5FF)",
        dialog: "linear-gradient(135deg, #0D1B3E 0%, #060D1F 100%)",
        tabActive: "linear-gradient(180deg, rgba(130,60,255,0.2) 0%, transparent 100%)",
        tabHover: "linear-gradient(180deg, rgba(130,60,255,0.15) 0%, transparent 100%)",
        choiceActive: "linear-gradient(135deg, rgba(130,60,255,0.35) 0%, rgba(0,229,255,0.1) 100%)",
        choiceHover: "linear-gradient(135deg, rgba(130,60,255,0.25) 0%, rgba(0,229,255,0.08) 100%)",
        primaryButton: "linear-gradient(135deg, rgba(130,60,255,0.5) 0%, rgba(0,229,255,0.25) 100%)",
        primaryButtonHover: "linear-gradient(135deg, rgba(130,60,255,0.65) 0%, rgba(0,229,255,0.35) 100%)",
        rowDivider: "linear-gradient(to right, transparent, rgba(150, 100, 220, 0.4), transparent)",
        leaderboardBg: `
          radial-gradient(ellipse at top, #0D1B3E 0%, #060D1F 70%),
          repeating-linear-gradient(0deg, transparent, transparent 39px, rgba(0,150,255,0.06) 40px),
          repeating-linear-gradient(90deg, transparent, transparent 39px, rgba(0,150,255,0.06) 40px)
        `,
    },
};

// ── Dialog ────────────────────────────────────────────────────────────────────

export const dialogPaperSx = {
    background: cyberColors.gradient.dialog,
    border: `2px solid ${cyberColors.purple.borderBold}`,
    borderRadius: "16px",
    boxShadow: `0 0 30px ${cyberColors.purple.glow}`,
};

export const dialogTitleSx = {
    fontFamily: CYBER_FONT,
    color: cyberColors.cyan,
    fontWeight: "700",
    borderBottom: `1px solid ${cyberColors.purple.borderMedium}`,
    pr: 6,
};

export const dialogContentSx = {
    pt: 2,
    color: cyberColors.muted.textBright,
};

export const innerPanelSx = {
    bgcolor: cyberColors.purple.faint,
    border: `1px solid ${cyberColors.purple.border}`,
    borderRadius: 2,
};

// ── Leaderboard layout ────────────────────────────────────────────────────────

export const leaderboardBackgroundSx = {
    width: "100%",
    height: "100%",
    backgroundImage: cyberColors.gradient.leaderboardBg,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    p: 3,
    boxSizing: "border-box",
};

export const leaderboardCardSx = {
    width: "100%",
    maxWidth: 700,
    border: `2px solid ${cyberColors.purple.borderBold}`,
    borderRadius: "16px",
    background: cyberColors.surface.dark,
    boxShadow: `0 0 20px ${cyberColors.purple.cardGlow}, inset 0 0 20px ${cyberColors.purple.insetGlow}`,
    p: 2,
    px: 3,
};

// ── Typography ────────────────────────────────────────────────────────────────

export const headingGradientSx = {
    fontFamily: CYBER_FONT,
    fontSize: "25px",
    fontWeight: "900",
    background: cyberColors.gradient.heading,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    letterSpacing: "0.05em",
};

export const subheadingSx = {
    fontFamily: CYBER_FONT,
    fontSize: "20px",
    fontWeight: "900",
    color: cyberColors.cyan,
    textShadow: `0 0 20px ${cyberColors.cyanAlpha.glow}`,
    letterSpacing: "0.03em",
};

export const bodyTextSx = {
    fontFamily: CYBER_FONT,
    fontSize: "20px",
    fontWeight: "700",
    color: cyberColors.cyan,
    textShadow: `0 0 8px ${cyberColors.cyanAlpha.medium}`,
};

export const mutedTextSx = {
    fontFamily: CYBER_FONT,
    color: cyberColors.text.empty,
    fontSize: "20px",
};

// ── Tabs ──────────────────────────────────────────────────────────────────────

export const TAB_BAR_HEIGHT = 48;

export const tabsBarSx = {
    display: "flex",
    height: TAB_BAR_HEIGHT,
    flexShrink: 0,
    borderTop: `1px solid ${cyberColors.purple.borderStrong}`,
    background: cyberColors.surface.darker,
};

export const tabItemSx = (isActive, isLast = false) => ({
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    position: "relative",
    borderRight: isLast ? "none" : `1px solid ${cyberColors.purple.borderMedium}`,
    background: isActive ? cyberColors.gradient.tabActive : "transparent",
    transition: "background 0.2s",
    "&:hover": {
        background: cyberColors.gradient.tabHover,
    },
});

export const tabLabelSx = (isActive) => ({
    fontFamily: CYBER_FONT,
    fontSize: "10px",
    fontWeight: isActive ? "900" : "500",
    color: isActive ? cyberColors.cyan : cyberColors.muted.text,
    textShadow: isActive ? `0 0 8px ${cyberColors.cyanAlpha.strong}` : "none",
    transition: "color 0.2s",
    userSelect: "none",
});

export const tabActiveIndicatorSx = {
    position: "absolute",
    top: 0,
    left: "10%",
    width: "80%",
    height: "3px",
    background: cyberColors.gradient.heading,
    borderRadius: "0 0 3px 3px",
    boxShadow: `0 0 8px ${cyberColors.cyanAlpha.glowStrong}`,
};

// ── Icon buttons ──────────────────────────────────────────────────────────────

export const iconButtonOverlaySx = {
    padding: "6px",
    background: cyberColors.surface.dark,
    border: `1px solid ${cyberColors.purple.glow}`,
    borderRadius: "6px",
    "&:hover": {
        background: cyberColors.purple.hoverBg,
        border: `1px solid ${cyberColors.purple.activeBorder}`,
    },
};

export const iconButtonCloseSx = {
    position: "absolute",
    right: 12,
    top: 12,
    color: cyberColors.muted.text,
    "&:hover": { color: cyberColors.cyan },
};

// ── Buttons ───────────────────────────────────────────────────────────────────

export const choiceButtonSx = (isActive) => ({
    fontFamily: CYBER_FONT,
    fontSize: CHOICE_BUTTON_FONT_SIZE,
    fontWeight: isActive ? "700" : "400",
    color: isActive ? cyberColors.cyan : cyberColors.muted.textBright,
    background: isActive ? cyberColors.gradient.choiceActive : cyberColors.purple.faint,
    border: isActive
        ? `1px solid ${cyberColors.cyanAlpha.strong}`
        : `1px solid ${cyberColors.purple.border}`,
    borderRadius: "8px",
    textTransform: "none",
    width: "100%",
    height: 120,
    lineHeight: 1.3,
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    px: 1,
    "&:hover": {
        background: cyberColors.gradient.choiceHover,
        color: cyberColors.cyan,
        border: `1px solid ${cyberColors.cyanAlpha.subtle}`,
    },
});

export const primaryButtonSx = {
    fontFamily: CYBER_FONT,
    fontWeight: "bold",
    textTransform: "none",
    background: cyberColors.gradient.primaryButton,
    color: cyberColors.text.white,
    border: `1px solid ${cyberColors.cyanAlpha.strong}`,
    borderRadius: 2,
    boxShadow: `0 0 12px ${cyberColors.purple.glow}`,
    "&:hover": {
        background: cyberColors.gradient.primaryButtonHover,
        boxShadow: `0 0 16px ${cyberColors.cyanAlpha.subtle}`,
    },
    "&.Mui-disabled": {
        background: cyberColors.purple.faint,
        color: cyberColors.text.disabled,
        border: `1px solid ${cyberColors.purple.border}`,
        boxShadow: "none",
    },
};

export const outlinedButtonSx = {
    fontFamily: CYBER_FONT,
    fontWeight: "bold",
    textTransform: "none",
    color: cyberColors.cyan,
    borderColor: cyberColors.cyanAlpha.strong,
    borderRadius: 2,
    "&:hover": {
        borderColor: cyberColors.cyan,
        bgcolor: cyberColors.purple.faint,
    },
};

// ── Form controls ─────────────────────────────────────────────────────────────

export const textFieldSx = {
    "& .MuiInputLabel-root": {
        color: cyberColors.muted.text,
        "&.Mui-focused": { color: cyberColors.cyan },
    },
    "& .MuiOutlinedInput-root": {
        color: cyberColors.cyan,
        fontFamily: CYBER_FONT,
        "& fieldset": {
            borderColor: cyberColors.purple.glow,
        },
        "&:hover fieldset": {
            borderColor: cyberColors.cyanAlpha.subtle,
        },
        "&.Mui-focused fieldset": {
            borderColor: cyberColors.cyan,
        },
        "&.Mui-disabled": {
            color: cyberColors.muted.text,
        },
    },
    "& .MuiFormHelperText-root": {
        fontFamily: CYBER_FONT,
        color: cyberColors.muted.textBright,
        "&.Mui-error": {
            color: "#ff6b8a",
        },
    },
};

// ── Misc ──────────────────────────────────────────────────────────────────────

export const rowDividerSx = {
    width: "100%",
    height: "1px",
    background: cyberColors.gradient.rowDivider,
    my: 0.5,
};

export const avatarSx = {
    bgcolor: cyberColors.purple.activeFill,
    border: `2px solid ${cyberColors.purple.borderBold}`,
    color: cyberColors.cyan,
};

export const loadingSpinnerSx = {
    color: cyberColors.muted.text,
};
